import { useMemo } from 'react';

import { getCookie } from '@zg-rentals/zillow-rental-manager/client';

import { ZRM_IDV_MOCK_STATE } from '../constants/FeatureFlagConstants';
import {
  getIdentityVerificationMock,
  isIdentityVerificationMockState,
} from '../constants/IdentityVerificationMockConstants';

import { useAuth } from '../components/AuthProvider';

import type { IdentityVerificationEntry, IdentityVerificationRestrictedAction } from '../types/identityVerification';

const DAY_MS = 24 * 60 * 60 * 1000;

export type IdentityVerification = {
  /** True until the current user loads; `entry` is undefined meanwhile, so don't read it as "not in the experiment". */
  isLoading: boolean;
  /** Undefined when the user is outside the IDV experiment. */
  entry: IdentityVerificationEntry | undefined;
  restrictedActions: Array<IdentityVerificationRestrictedAction>;
  /** True when any action is restricted; gate a specific action on `restrictedActions` instead. */
  isEnforced: boolean;
  /** Whole days until the grace period ends, counting a partial day as one; 0 once it has passed. */
  daysLeft: number | undefined;
};

export const getDaysLeft = (endsAt: number, now: number): number => Math.max(0, Math.ceil((endsAt - now) / DAY_MS));

// Same rule as the /featureFlags page; QA runs on qa.zillow.net, so only prod ignores the mock cookie.
const isProductionHost = () =>
  typeof window !== 'undefined' &&
  (window.location.hostname.includes('zillow.com') || window.location.hostname.includes('hotpads.com'));

export const useIdentityVerification = (): IdentityVerification => {
  const auth = useAuth();
  const isUserPending = 'isPending' in auth && auth.isPending;
  const mockState = isProductionHost() ? undefined : getCookie(ZRM_IDV_MOCK_STATE);

  return useMemo(() => {
    const now = Date.now();
    const isMocked = isIdentityVerificationMockState(mockState);
    const entry = isMocked ? getIdentityVerificationMock(mockState, now) : undefined;
    const restrictedActions = entry?.restrictedActions ?? [];
    const gracePeriodEndsAt = entry?.enrollment?.gracePeriodEndsAt;

    return {
      isLoading: !isMocked && isUserPending,
      entry,
      restrictedActions,
      isEnforced: restrictedActions.length > 0,
      daysLeft: gracePeriodEndsAt === undefined ? undefined : getDaysLeft(gracePeriodEndsAt, now),
    };
  }, [mockState, isUserPending]);
};
